'use client';

import React from 'react';
import { Users, CheckCircle2, Mail, UserPlus, Shield } from 'lucide-react';

const statsBadges = [
  { icon: <CheckCircle2 className="w-4 h-4 text-primary" />, text: 'Direct Access' },
  { icon: <UserPlus className="w-4 h-4 text-primary" />, text: '10+ Providers' },
  { icon: <Shield className="w-4 h-4 text-primary" />, text: '24h Return Policy' },
];

// Tailwind delay classes for staggering (reduced for better performance)
const featureDelays = ['delay-[300ms]', 'delay-[350ms]', 'delay-[400ms]'];

export default function HeroSection(): React.ReactElement {
  return (
    <div className="bg-background">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4 sm:py-5 md:py-6 lg:py-8 pb-8 sm:pb-10">
        {/* Stats badges */}
        <div className="flex flex-wrap justify-center gap-2 md:gap-3 mb-4 md:mb-5 py-1">
          {statsBadges.map((badge, index) => (
            <div
              key={index}
              // Apply animation classes directly with stagger delays (match lower badges)
              className={`
                flex items-center gap-1.5 px-3 py-2 bg-card border border-[hsl(var(--border))] text-foreground rounded-lg shadow-sm
                animate-fade-in-up duration-500 ease-out ${featureDelays[index % featureDelays.length]}
                transition duration-200 hover:bg-accent/10 hover:shadow-[0_0_16px_0_rgba(59,130,246,0.25)]
              `}
            >
              <span className="bg-accent/10 p-1.5 rounded-full">{badge.icon}</span>
              <span className="block font-medium text-sm min-w-0">{badge.text}</span>
            </div>
          ))}
        </div>

        {/* Main Hero Content - Removed delay for better LCP */}
        <div
          className={`
            text-center space-y-3 sm:space-y-4 max-w-5xl mx-auto
            animate-fade-in duration-500 ease-out
           `}
        >
          <div className="space-y-2 sm:space-y-3">
            <h1 className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-extrabold tracking-tight text-foreground leading-tight px-1 sm:px-0">
              {/* H1 - Line 1 - Reduced delay for better LCP */}
              <span
                className={`
                  block text-blue-600 dark:text-blue-400
                  animate-fade-in-up duration-500 ease-out
                `}
              >
                Leads, Scrapers, Group Buys
              </span>
              {/* H1 - Line 2 - Reduced delay for better LCP */}
              <span
                className={`
                  block text-primary
                  animate-fade-in-up duration-500 delay-[100ms] ease-out
                 `}
              >
                & More: All In One Place
              </span>
            </h1>

            {/* Paragraph - LCP Element (removed animation delay) */}
            <p className="text-sm sm:text-base md:text-lg lg:text-xl text-muted-foreground max-w-3xl mx-auto leading-relaxed font-light px-3 sm:px-2 md:px-0">
              Instantly buy verified lists, hire scrapers, join group buys for savings, or use our
              platform services.
            </p>
          </div>

          {/* Fee badge - Reduced delay for better overall performance */}
          <div
            className={`
              flex justify-center mt-4
              animate-fade-in duration-500 delay-[400ms] ease-out
             `}
          >
            <div className="inline-flex items-center px-3 py-1.5 bg-green-100 dark:bg-green-900/30 text-green-800 dark:text-green-300 rounded-full text-sm">
              <CheckCircle2 className="w-4 h-4 mr-1.5" />
              Free to list • 10% fee on sales
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
